import { SmartRenderer } from '@fusioncharts/core/src/component-interface';
import sankeyNodeFactory from '../../factories/sankey-node-factory';
import sankeyLinkFactory from '../../factories/sankey-link-factory';
import { createNodeDimensions } from './sankeyhelper';
import { addDep } from '@fusioncharts/core/src/dependency-manager';
import linkAnimation from './index.animation';

let GUTTER5 = 5,
  START = 'start',
  INSIDE = 'inside',
  END = 'end',
  BLANK = '',
  HORIZONTAL = 'horizontal',
  NODEROLLOVER = 'dataPlotRollOver',
  NODEROLLOUT = 'dataPlotRollOut',
  NODECLICK = 'dataPlotClick',
  LINKROLLOVER = 'linkRollOver',
  LINKROLLOUT = 'linkRollOut',
  LINKCLICK = 'linkClick';

addDep({
  name: 'sankeyLinkAnimation',
  type: 'animationRule',
  extension: linkAnimation
});
class Sankey extends SmartRenderer {
  constructor () {
    super();

    this.registerFactory('node', sankeyNodeFactory);
    this.registerFactory('link', sankeyLinkFactory, ['node']);
  }
  static getName () {
    return 'sankey';
  }

  getName () {
    return 'sankey';
  }

  getType () {
    return 'dataset';
  }

  configureAttributes (inputConfig = {}) {
    Object.assign(this.config, inputConfig);
  }

  _addLegend () {
    let dataset = this,
      dsConfig = dataset.config,
      nodes = dataset.getChildren('nodes'),
      legend = dataset.getFromEnv('legend'),
      nodeStyle,
      nodeConfig,
      legendItem,
      legendItemId,
      legendItemMap = dsConfig.legendItemMap = dsConfig.legendItemMap || {},
      curLegendItems = new Set();

    Object.keys(legendItemMap).forEach(key => curLegendItems.add(legendItemMap[key]));
    nodes.forEach(node => {
      legendItemId = legendItemMap[node.getId()];
      legendItem = legend.getItem(legendItemId);
      if (!legendItem) {
        legendItemId = legend.createItem(dataset);
        legendItem = legend.getItem(legendItemId);
        legendItemMap[node.getId()] = legendItemId;
      } else {
        // eslint-disable-next-line
        curLegendItems.delete(legendItemId);
      }

      legendItem.configure({
        enabled: true,
        label: node.config.label
      });

      node.setData({
        legendItemId
      });

      nodeConfig = node.config;
      nodeStyle = nodeConfig.style;

      legendItem.setStateCosmetics('default', {
        symbol: {
          fill: nodeStyle.node.normal.fill,
          rawFillColor: nodeConfig.rawCosmetics.fill,
          stroke: nodeStyle.node.normal.stroke,
          cursor: 'default'
        },
        text: {
          cursor: 'default'
        }
      });
    });

    curLegendItems.forEach(item => {
      legend.disposeItem(item);
    });
  }

  allocatePosition () {
    let dataset = this,
      bounds,
      chartConfig = dataset.getFromEnv('chartConfig'),
      sankeyGraph = dataset.getFromEnv('sankey-graph'),
      orientation = dataset.getFromEnv('orientation'),
      {
        canvasLeft,
        canvasTop,
        canvasHeight,
        canvasWidth
      } = chartConfig,
      columnWidth = 0,
      columnHeight = 0,
      columns = 0,
      { nodeWidth, nodeSpacing, nodeRelaxation, style } = dataset.config,
      nodeLabelStyle = style.node.label;

    bounds = {
      left: canvasLeft,
      top: canvasTop,
      right: canvasLeft + canvasWidth,
      bottom: canvasTop + canvasHeight
    };

    dataset.config.nodeLabelCosmetics = {
      'font-size': this.computeFontSize(nodeLabelStyle.fontSize),
      'font-family': nodeLabelStyle.fontFamily,
      'font-weight': nodeLabelStyle.fontWeight,
      'font-style': nodeLabelStyle.fontStyle
    };

    if (orientation === HORIZONTAL) {
      dataset._accomodateBoundaryHLabels(bounds);

      createNodeDimensions(sankeyGraph, {
        width: [bounds.left, bounds.right],
        height: [bounds.top, bounds.bottom],
        nodeGutter: nodeSpacing,
        orientation,
        nodeRelaxation
      });

      sankeyGraph.nodes.forEach(node => {
        columns = Math.max(columns, node.props.depth);
      });

      columnWidth = dataset.config.columnGap = Math.max((bounds.right - bounds.left - nodeWidth * columns) / Math.max(columns, 1), 0);
      dataset._accomodateInternalHLabels(columnWidth);
    } else {
      dataset._accomodateBoundaryVLabels(bounds);

      createNodeDimensions(sankeyGraph, {
        width: [bounds.left, bounds.right],
        height: [bounds.top, bounds.bottom],
        nodeGutter: nodeSpacing,
        orientation,
        nodeRelaxation
      });

      sankeyGraph.nodes.forEach(node => {
        columns = Math.max(columns, node.props.depth);
      });

      columnHeight = dataset.config.columnGap = Math.max((bounds.bottom - bounds.top - nodeWidth * columns) / Math.max(columns, 1), 0);
      dataset._accomodateInternalVLabels(columnHeight, bounds.bottom - bounds.top);
    }
  }

  _accomodateBoundaryHLabels (bounds) {
    let dataset = this,
      dsConfig = dataset.config,
      { labelSepChar } = dsConfig,
      nodes = dataset.getChildren('nodes'),
      sl = dataset.getFromEnv('smartLabel'),
      canvasWidth = bounds.right - bounds.left,
      labelSl,
      valueSl,
      space,
      spaceFactor,
      availableWidth,
      divFactor,
      formattedValueSl,
      maxLeftSpaceReq = 0,
      maxRightSpaceReq = 0;

    nodes.forEach(node => {
      const { showValue, showLabel, label, props, labelPosition, value, rotateLabel, labelPadding } = node.config,
        { sourceLinks, targetLinks } = props;

      sl.setStyle(dataset.config.nodeLabelCosmetics);
      valueSl = sl.getSize(showValue ? value : BLANK);
      formattedValueSl = sl.getSize(showValue ? labelSepChar + ' ' + value : BLANK);
      availableWidth = Math.max(0, canvasWidth - formattedValueSl.width);

      if (sourceLinks.length && !targetLinks.length) {
        if (labelPosition === END) return;

        spaceFactor = labelPosition === START ? 0.1 : 0.2;
        divFactor = spaceFactor * 10;
        labelSl = sl.getSmartText(showLabel ? label : BLANK, spaceFactor * availableWidth, sl._lineHeight);

        if (rotateLabel) {
          space = labelPosition === INSIDE ? 0 : Math.max(labelSl.height / divFactor, valueSl.height / divFactor);
          maxLeftSpaceReq = Math.max(
            maxLeftSpaceReq,
            space + ((space && labelPosition === START) ? labelPadding : 0)
          );
        } else {
          space = (labelSl.width + (labelSl.width ? formattedValueSl.width : valueSl.width)) / divFactor;
          maxLeftSpaceReq = Math.max(
            maxLeftSpaceReq,
            space + ((space && labelPosition === START) ? labelPadding : 0)
          );
        }
        node.setData({ formattedLabel: labelSl });
      } else if (!sourceLinks.length && targetLinks.length) {
        if (labelPosition === START) return;

        spaceFactor = labelPosition === END ? 0.1 : 0.2;
        divFactor = spaceFactor * 10;
        labelSl = sl.getSmartText(showLabel ? label : BLANK, spaceFactor * availableWidth, sl._lineHeight);

        if (rotateLabel) {
          space = labelPosition === INSIDE ? 0 : Math.max(labelSl.height / divFactor, valueSl.height / divFactor);
          maxRightSpaceReq = Math.max(
            maxRightSpaceReq,
            space + ((space && labelPosition === END) ? labelPadding : 0)
          );
        } else {
          space = (labelSl.width + (labelSl.width ? formattedValueSl.width : valueSl.width)) / divFactor;
          maxRightSpaceReq = Math.max(
            maxRightSpaceReq,
            space + ((space && labelPosition === END) ? labelPadding : 0)
          );
        }
        node.setData({ formattedLabel: labelSl });
      }
    });

    bounds.left += maxLeftSpaceReq + (maxLeftSpaceReq ? GUTTER5 : 0);
    bounds.right -= (maxRightSpaceReq + (maxRightSpaceReq ? GUTTER5 : 0));

    return bounds;
  }

  _accomodateInternalHLabels (maxColumnWidth) {
    let dataset = this,
      nodes = dataset.getChildren('nodes'),
      sl = dataset.getFromEnv('smartLabel'),
      availableWidth,
      { labelSepChar } = dataset.config,
      formattedValueSl,
      labelSl;

    nodes.forEach(node => {
      let { showValue, showLabel, label, value, props, rotateLabel, labelPosition, labelPadding } = node.config,
        { sourceLinks, targetLinks } = props,
        nodeHeight = Math.max(0, props.y1 - props.y0);

      label = showLabel ? label : BLANK;
      value = showValue ? value : BLANK;
      sl.setStyle(dataset.config.nodeLabelCosmetics);
      formattedValueSl = sl.getSize(showValue ? labelSepChar + ' ' + value : BLANK);
      availableWidth = Math.max(0, maxColumnWidth * 0.75 - labelPadding - formattedValueSl.width);

      if (rotateLabel) {
        labelSl = sl.getSmartText(label, nodeHeight - formattedValueSl.width, sl._lineHeight);
        node.setData({ formattedLabel: labelSl });
        return;
      }

      if (
        (sourceLinks.length && !targetLinks.length && labelPosition === END) ||
        (!sourceLinks.length && targetLinks.length && labelPosition === START) ||
        (sourceLinks.length && targetLinks.length) ||
        (!sourceLinks.length && !targetLinks.length)
      ) {
        labelSl = sl.getSmartText(label, availableWidth, sl._lineHeight);
        node.setData({ formattedLabel: labelSl });
      }
    });
  }

  _accomodateBoundaryVLabels (bounds) {
    let dataset = this,
      nodes = dataset.getChildren('nodes'),
      sl = dataset.getFromEnv('smartLabel'),
      labelSepChar = dataset.config.labelSepChar,
      canvasHeight = bounds.bottom - bounds.top,
      valueSl,
      labelSl,
      spaceFactor,
      space,
      availableHeight,
      divFactor,
      formattedValueSl,
      maxTopSpaceReq = 0,
      maxBottomSpaceReq = 0;

    nodes.forEach(node => {
      let { props, labelPosition, rotateLabel, showValue, showLabel, value, label, labelPadding } = node.config,
        { sourceLinks, targetLinks } = props;

      value = showValue ? value : BLANK;
      label = showLabel ? label : BLANK;

      sl.setStyle(dataset.config.nodeLabelCosmetics);
      valueSl = sl.getSize(value);
      formattedValueSl = sl.getSize(showValue ? labelSepChar + ' ' + value : BLANK);
      availableHeight = Math.max(0, canvasHeight - formattedValueSl.width);

      if (sourceLinks.length && !targetLinks.length) {
        if (labelPosition === END) return;

        spaceFactor = labelPosition === START ? 0.1 : 0.2;
        divFactor = spaceFactor * 10;
        labelSl = sl.getSmartText(label, spaceFactor * availableHeight, sl._lineHeight);

        if (rotateLabel) {
          space = (labelSl.width + (labelSl.width ? formattedValueSl.width : valueSl.width)) / divFactor;
          maxTopSpaceReq = Math.max(
            maxTopSpaceReq,
            space + ((space && labelPosition === START) ? labelPadding : 0)
          );
        } else {
          space = labelPosition === INSIDE ? 0 : Math.max(labelSl.height / divFactor, valueSl.height / divFactor);
          maxTopSpaceReq = Math.max(
            maxTopSpaceReq,
            space + ((space && labelPosition === START) ? labelPadding : 0)
          );
        }
        node.setData({ formattedLabel: labelSl });
      } else if (!sourceLinks.length && targetLinks.length) {
        if (labelPosition === START) return;

        spaceFactor = labelPosition === END ? 0.1 : 0.2;
        divFactor = spaceFactor * 10;
        labelSl = sl.getSmartText(label, spaceFactor * availableHeight, sl._lineHeight);

        if (rotateLabel) {
          space = (labelSl.width + (labelSl.width ? formattedValueSl.width : valueSl.width)) / divFactor;
          maxBottomSpaceReq = Math.max(
            maxBottomSpaceReq,
            space + ((space && labelPosition === END) ? labelPadding : 0)
          );
        } else {
          space = labelPosition === INSIDE ? 0 : Math.max(labelSl.height / divFactor, valueSl.height / divFactor);
          maxBottomSpaceReq = Math.max(
            maxBottomSpaceReq,
            space + ((space && labelPosition === END) ? labelPadding : 0)
          );
        }
        node.setData({ formattedLabel: labelSl });
      }
    });

    bounds.top += maxTopSpaceReq + (maxTopSpaceReq ? GUTTER5 : 0);
    bounds.bottom -= (maxBottomSpaceReq + (maxBottomSpaceReq ? GUTTER5 : 0));

    return bounds;
  }

  _accomodateInternalVLabels (maxColumnWidth) {
    let dataset = this,
      nodes = dataset.getChildren('nodes'),
      sl = dataset.getFromEnv('smartLabel'),
      labelSepChar = dataset.config.labelSepChar,
      availableHeight,
      labelSl,
      nodeWidth,
      formattedValueSl;

    nodes.forEach(node => {
      let { showValue, props, label, value, showLabel, rotateLabel, labelPosition, labelPadding } = node.config,
        { sourceLinks, targetLinks } = props;

      sl.setStyle(dataset.config.nodeLabelCosmetics);
      label = showLabel ? label : BLANK;
      value = showValue ? value : BLANK;
      formattedValueSl = sl.getSize(showValue ? labelSepChar + ' ' + value : BLANK);
      availableHeight = Math.max(0, maxColumnWidth * 0.75 - labelPadding - formattedValueSl.width);

      nodeWidth = props.x1 - props.x0;
      if (!rotateLabel) {
        labelSl = sl.getSmartText(label, nodeWidth - formattedValueSl.width, sl._lineHeight);
        node.setData({ formattedLabel: labelSl });
        return;
      }

      if (
        (sourceLinks.length && !targetLinks.length && labelPosition === END) ||
        (!sourceLinks.length && targetLinks.length && labelPosition === START) ||
        (sourceLinks.length && targetLinks.length) ||
        (!sourceLinks.length && !targetLinks.length)
      ) {
        labelSl = sl.getSmartText(label, availableHeight, sl._lineHeight);
        node.setData({ formattedLabel: labelSl });
      }
    });
  }

  attachListeners () {
    let dataset = this,
      nodes = dataset.getChildren('nodes'),
      links = dataset.getChildren('links');

    nodes.forEach(node => {
      node.onFocusCallback = node.onFocusCallback || dataset.interactionCallback(node, 'focus');
      node.onFocusOutCallback = node.onFocusOutCallback || dataset.interactionCallback(node, 'normal');
      node.onClickCallback = node.onClickCallback || dataset.interactionCallback(node, 'click');
      node.dragStartCallback = node.dragStartCallback || dataset.dragCallback(node, 'start');
      node.dragMoveCallback = node.dragMoveCallback || dataset.dragCallback(node, 'move');
      node.dragEndCallback = node.dragEndCallback || dataset.dragCallback(node, 'end');

      node.addEventListener('fc-mouseover', node.onFocusCallback);
      node.addEventListener('fc-mouseout', node.onFocusOutCallback);
      node.addEventListener('fc-click', node.onClickCallback);

      node.addEventListener('fc-dragstart', node.dragStartCallback);
      node.addEventListener('fc-dragmove', node.dragMoveCallback);
      node.addEventListener('fc-dragend', node.dragEndCallback);
    });

    links && links.forEach(link => {
      link.onFocusCallback = link.onFocusCallback || dataset.interactionCallback(link, 'focus');
      link.onFocusOutCallback = link.onFocusOutCallback || dataset.interactionCallback(link, 'normal');
      link.onClickCallback = link.onClickCallback || dataset.interactionCallback(link, 'click');

      link.addEventListener('fc-mouseover', link.onFocusCallback);
      link.addEventListener('fc-mouseout', link.onFocusOutCallback);
      link.addEventListener('fc-click', link.onClickCallback);
    });
  }

  interactionCallback (component, mode) {
    let dataset = this,
      dsConfig = dataset.config,
      chart = dataset.getLinkedParent(),
      type = component.getType();
    if (type === 'node') {
      return function () {
        let nodes = dataset.getChildren('nodes'),
          links = dataset.getChildren('links') || [],
          eventArgs = Object.assign({}, component.config.eventArgs),
          componentId = component.getId();

        if (component.config.isDragged) return;

        if (mode === 'click') {
          chart.fireChartInstanceEvent(NODECLICK, eventArgs);
        } else if (mode === 'normal') {
          chart.fireChartInstanceEvent(NODEROLLOUT, eventArgs);

          if (!dsConfig.highlightEffect) return;

          nodes.forEach(node => node.setData({ mode: 'normal' }));
          links.forEach(link => link.setData({ mode: 'normal' }));
        } else {
          let focusNodes = new Set(),
            { sourceLinks, targetLinks } = component.config.props;

          chart.fireChartInstanceEvent(NODEROLLOVER, eventArgs);

          if (!dsConfig.highlightEffect) return;

          focusNodes.add(componentId);
          sourceLinks.forEach(link => {
            focusNodes.add(link.to);
          });
          targetLinks.forEach(link => {
            focusNodes.add(link.from);
          });

          nodes.forEach(node => {
            if (focusNodes.has(node.getId())) {
              node.setData({
                mode: 'focus'
              });
            } else {
              node.setData({
                mode: 'focusOut'
              });
            }
          });

          links.forEach(link => {
            let { source, target } = link.config.props;
            if (componentId === source.id || componentId === target.id) {
              link.setData({
                mode: 'focus'
              });
            } else {
              link.setData({
                mode: 'focusOut'
              });
            }
          });
        }
      };
    }
    return function () {
      let nodes = dataset.getChildren('nodes'),
        links = dataset.getChildren('links') || [],
        eventArgs = Object.assign({}, component.config.eventArgs);

      if (component.config.isDragged) return;

      if (mode === 'click') {
        chart.fireChartInstanceEvent(LINKCLICK, eventArgs);
      } else if (mode === 'normal') {
        chart.fireChartInstanceEvent(LINKROLLOUT, eventArgs);

        if (!dsConfig.highlightEffect) return;

        nodes.forEach(node => node.setData({ mode: 'normal' }));
        links.forEach(link => link.setData({ mode: 'normal' }));
      } else {
        let focusNodes = new Set(),
          { source, target } = component.config.props;

        chart.fireChartInstanceEvent(LINKROLLOVER, eventArgs);

        if (!dsConfig.highlightEffect) return;

        focusNodes.add(source.id);
        focusNodes.add(target.id);
        nodes.forEach(node => {
          if (focusNodes.has(node.getId())) {
            node.setData({
              mode: 'focus'
            });
          } else {
            node.setData({
              mode: 'focusOut'
            });
          }
        });

        links.forEach(link => link.setData({ mode: 'focusOut' }));
        component.setData({
          mode: 'focus'
        });
      }
    };
  }

  dragCallback (component, mode) {
    let dataset = this,
      dsConfig = dataset.config;

    if (mode === 'start') {
      return function () {
        let { props } = component.config,
          linkComponent,
          linkProps;

        if (!dsConfig.enableDrag) return;

        component.onFocusOutCallback();
        component.setData({
          isDragged: true,
          curPos: {
            x0: props.x0,
            x1: props.x1,
            y0: props.y0,
            y1: props.y1
          }
        });

        props.sourceLinks.forEach(link => {
          linkComponent = dataset.getChild(link.props.index, 'links');
          linkProps = linkComponent.config.props;

          linkComponent.setData({
            isDragged: true,
            curPos: {
              x0: linkProps.x0,
              x1: linkProps.x1,
              y0: linkProps.y0,
              y1: linkProps.y1
            }
          });
        });
        props.targetLinks.forEach(link => {
          linkComponent = dataset.getChild(link.props.index, 'links');
          linkProps = linkComponent.config.props;

          linkComponent.setData({
            isDragged: true,
            curPos: {
              x0: linkProps.x0,
              x1: linkProps.x1,
              y0: linkProps.y0,
              y1: linkProps.y1
            }
          });
        });
      };
    } else if (mode === 'end') {
      return function () {
        let { props } = component.config,
          linkComponent;

        if (!dsConfig.enableDrag) return;

        component.setData({
          isDragged: false
        });

        props.sourceLinks.forEach(link => {
          linkComponent = dataset.getChild(link.props.index, 'links');
          linkComponent.setData({
            isDragged: false
          });
        });
        props.targetLinks.forEach(link => {
          linkComponent = dataset.getChild(link.props.index, 'links');
          linkComponent.setData({
            isDragged: false
          });
        });
      };
    }
    return function (evt) {
      let data = evt.originalEvent.data,
        dx = data[0],
        dy = data[1],
        linkComponent,
        linkProps,
        linkCurPos,
        orientation = dataset.getFromEnv('orientation'),
        { canvasLeft, canvasRight, canvasTop, canvasBottom } = dataset.getFromEnv('chartConfig'),
        { curPos, props } = component.config;

      if (!dsConfig.enableDrag) return;

      if (orientation === HORIZONTAL) {
        if (curPos.y0 + dy < canvasTop || curPos.y1 + dy > canvasBottom) return;

        props.y0 = curPos.y0 + dy;
        props.y1 = curPos.y1 + dy;

        props.sourceLinks.forEach(link => {
          linkComponent = dataset.getChild(link.props.index, 'links');

          linkProps = linkComponent.config.props;
          linkCurPos = linkComponent.config.curPos;

          linkProps.y0 = linkCurPos.y0 + dy;
          linkComponent.setData({});
        });
        props.targetLinks.forEach(link => {
          linkComponent = dataset.getChild(link.props.index, 'links');

          linkProps = linkComponent.config.props;
          linkCurPos = linkComponent.config.curPos;

          linkProps.y1 = linkCurPos.y1 + dy;
          linkComponent.setData({});
        });
      } else {
        if (curPos.x0 + dx < canvasLeft || curPos.x1 + dx > canvasRight) return;

        props.x0 = curPos.x0 + dx;
        props.x1 = curPos.x1 + dx;

        props.sourceLinks.forEach(link => {
          linkComponent = dataset.getChild(link.props.index, 'links');

          linkProps = linkComponent.config.props;
          linkCurPos = linkComponent.config.curPos;

          linkProps.x0 = linkCurPos.x0 + dx;
          linkComponent.setData({});
        });
        props.targetLinks.forEach(link => {
          linkComponent = dataset.getChild(link.props.index, 'links');

          linkProps = linkComponent.config.props;
          linkCurPos = linkComponent.config.curPos;

          linkProps.x1 = linkCurPos.x1 + dx;
          linkComponent.setData({});
        });
      }
      component.setData({});
    };
  }

  getClip (mode) {
    let dataset = this,
      orientation = dataset.getFromEnv('orientation'),
      clipAr = [],
      { canvasTop, canvasLeft, canvasWidth, canvasHeight } = dataset.getFromEnv('chartConfig');

    if (orientation === HORIZONTAL) {
      clipAr.push(canvasLeft);
      clipAr.push(canvasTop);
      if (mode === 'init') {
        clipAr.push(0);
      } else {
        clipAr.push(canvasWidth);
      }
      clipAr.push(canvasHeight);
    } else {
      clipAr.push(canvasLeft);
      clipAr.push(canvasTop);
      clipAr.push(canvasWidth);
      if (mode === 'init') {
        clipAr.push(0);
      } else {
        clipAr.push(canvasHeight);
      }
    }

    return clipAr;
  }

  draw () {
    let dataset = this;

    dataset.addGraphicalElement({
      el: 'group',
      attr: {
        name: 'sankey-link-group',
        'clip-rect': dataset.getClip('canvas')
      },
      id: 'sankey-link-group',
      component: dataset,
      container: {
        id: 'plotGroup',
        isParent: true
      },
      label: 'linkGroup'
    });

    dataset.addGraphicalElement({
      el: 'group',
      attr: {
        name: 'sankey-node-group'
      },
      id: 'sankey-node-group',
      component: dataset,
      container: {
        id: 'plotGroup',
        isParent: true
      },
      label: 'nodeGroup'
    });

    dataset.addGraphicalElement({
      el: 'group',
      attr: {
        name: 'sankey-node-label-group',
        ...dataset.config.nodeLabelCosmetics
      },
      id: 'sankey-node-label-group',
      component: dataset,
      container: {
        id: 'datalabelsGroup',
        isParent: true
      },
      label: 'nodeLabelGroup'
    });
  }
}

export default Sankey;
